import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Heart, MessageCircle, TrendingUp, Bell, Volume2, VolumeX } from 'lucide-react';
import { SectionHeader } from './HowItWorksSection';
import { playNotificationSound, playSparkSound } from '../lib/sparkSound';

const notifications = [
  {
    id: 1,
    icon: Sparkles,
    title: '🔥 Você recebeu uma nova Spark',
    desc: 'Roberto C. quer apoiar sua Cafeteria Beatriz.',
    time: 'agora',
    color: 'gold',
    isSpark: true,
  },
  {
    id: 2,
    icon: Heart,
    title: '⚡ Investidor interessado no seu negócio',
    desc: 'Helena M. curtiu sua ideia.',
    time: '2 min',
    color: 'blue',
    isSpark: false,
  },
  {
    id: 3,
    icon: TrendingUp,
    title: '✨ Deu Spark com um novo investidor',
    desc: 'Diego A. aceitou conversar com você.',
    time: '7 min',
    color: 'gold',
    isSpark: true,
  },
  {
    id: 4,
    icon: MessageCircle,
    title: '🚀 Sua oportunidade começou',
    desc: 'Conversa privada com Roberto C. liberada.',
    time: '12 min',
    color: 'blue',
    isSpark: false,
  },
  {
    id: 5,
    icon: MessageCircle,
    title: '💬 Nova mensagem recebida',
    desc: '"Adorei sua ideia! Quando conseguimos conversar?"',
    time: '1h',
    color: 'blue',
    isSpark: false,
  },
];

const NotificationsSection = () => {
  const [muted, setMuted] = useState(true);
  const [shown, setShown] = useState([0]);

  // Cycle: show 1, then 1+2, then 1+2+3, then 1+2+3+4, reset.
  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      i = (i + 1) % (notifications.length + 1);
      if (i === 0) {
        setShown([0]);
      } else {
        setShown((prev) => {
          const next = [...prev];
          if (!next.includes(i)) next.push(i);
          if (next.length > 4) next.shift();
          return next;
        });
        if (!muted) {
          if (notifications[i] && notifications[i].isSpark) {
            playSparkSound().catch(() => {});
          } else {
            playNotificationSound().catch(() => {});
          }
        }
      }
    }, 2400);
    return () => clearInterval(id);
  }, [muted]);

  return (
    <section id="notifications" className="relative py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] rounded-full" style={{
          background: 'radial-gradient(circle, rgba(255,167,38,0.12), transparent 70%)'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          gold
          eyebrow="Notificações"
          title={<>Cada notificação é uma <span className="gradient-text-gold">nova oportunidade</span>.</>}
          subtitle="Receba avisos no momento certo: alguém curtiu sua ideia, um investidor quer conversar, uma nova Spark nasceu."
        />

        <div className="mt-16 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: Phone with notifications */}
          <NotificationPhone shown={shown} />

          {/* Right: Description */}
          <div>
            <div className="space-y-4">
              {notifications.map((n, i) => (
                <motion.div
                  key={n.id}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className={`rounded-2xl p-4 flex items-start gap-3 ${
                    n.isSpark ? 'glass-card-gold' : 'glass-card-blue'
                  }`}
                >
                  <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                    n.isSpark ? 'bg-[#ffa726]/20 text-[#ffd54f]' : 'bg-[#4facfe]/20 text-[#4facfe]'
                  }`}>
                    <n.icon size={16} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-white">{n.title}</div>
                      <div className="text-[10px] text-slate-500">{n.time}</div>
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">{n.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            <button
              onClick={async () => {
                const next = !muted;
                setMuted(next);
                if (!next) {
                  await playNotificationSound().catch(() => {});
                }
              }}
              className="mt-6 inline-flex items-center gap-2 text-xs text-slate-300 hover:text-white transition-colors px-4 py-2 rounded-full border border-white/10 hover:border-[#ffa726]/50 bg-white/[0.03] backdrop-blur-md"
            >
              {muted ? <VolumeX size={14} /> : <Volume2 size={14} className="text-[#ffa726]" />}
              {muted ? 'Ativar som das notificações' : 'Som ativado'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const NotificationPhone = ({ shown }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.9 }}
      className="relative mx-auto w-full max-w-[320px] lg:max-w-[340px]"
    >
      <div className="absolute -inset-8 bg-gradient-to-br from-[#ffa726]/15 to-[#4facfe]/15 rounded-full blur-3xl" />

      <div className="relative bg-gradient-to-b from-[#1a1f3a] to-[#0a0e1f] rounded-[2.5rem] p-2 border border-white/10 shadow-2xl glow-mix">
        <div className="relative w-full aspect-[9/19] bg-gradient-to-b from-[#0a0e1f] via-[#050816] to-[#0a0e1f] rounded-[2rem] overflow-hidden">
          {/* Lockscreen wallpaper-ish gradient */}
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(circle at 50% 20%, rgba(79,172,254,0.25), transparent 50%), radial-gradient(circle at 50% 80%, rgba(255,167,38,0.18), transparent 50%)'
          }} />

          {/* Notch */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 bg-black rounded-full z-30" />

          {/* Status bar */}
          <div className="absolute top-3 left-0 right-0 px-6 flex items-center justify-between text-[9px] text-white/80 font-medium z-20">
            <span>9:41</span>
            <span></span>
          </div>

          {/* Time / date */}
          <div className="absolute top-12 left-0 right-0 text-center z-10">
            <div className="text-[10px] text-white/70 font-medium tracking-wide uppercase">terça, 12 de novembro</div>
            <div className="text-5xl font-extralight text-white mt-1">9:41</div>
          </div>

          {/* Notifications stack */}
          <div className="absolute bottom-0 left-0 right-0 px-3 pb-6 space-y-2 z-20">
            <AnimatePresence>
              {shown.map((idx) => {
                const n = notifications[idx];
                if (!n) return null;
                return (
                  <motion.div
                    key={n.id}
                    initial={{ opacity: 0, y: 40, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, x: 200 }}
                    transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                    className="relative rounded-2xl px-3 py-2.5"
                    style={{
                      background: 'rgba(20, 25, 50, 0.55)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(24px)',
                      WebkitBackdropFilter: 'blur(24px)',
                      boxShadow: n.isSpark
                        ? '0 8px 30px rgba(255,167,38,0.25), inset 0 1px 0 rgba(255,255,255,0.07)'
                        : '0 8px 30px rgba(79,172,254,0.18), inset 0 1px 0 rgba(255,255,255,0.07)',
                    }}
                  >
                    {/* Animated glow border when isSpark */}
                    {n.isSpark && (
                      <motion.div
                        animate={{ opacity: [0.4, 1, 0.4] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="absolute inset-0 rounded-2xl pointer-events-none"
                        style={{
                          boxShadow: '0 0 22px rgba(255,167,38,0.45), inset 0 0 12px rgba(255,167,38,0.18)'
                        }}
                      />
                    )}
                    <div className="relative flex items-center gap-2.5">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold ${
                        n.isSpark
                          ? 'bg-gradient-to-br from-[#ffa726] to-[#ff7043] text-black'
                          : 'bg-gradient-to-br from-[#4facfe] to-[#1e90ff] text-white'
                      }`}>
                        {n.isSpark ? '✨' : <Bell size={12} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <div className="text-[10px] font-bold text-white leading-tight">GetSpark</div>
                          <div className="text-[8px] text-white/60">{n.time}</div>
                        </div>
                        <div className="text-[10px] text-white font-semibold leading-tight mt-0.5 truncate">{n.title}</div>
                        <div className="text-[9px] text-white/70 truncate">{n.desc}</div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {/* Floating sparks particles when isSpark visible */}
          <FloatingSparks shown={shown} />
        </div>
      </div>
    </motion.div>
  );
};

const FloatingSparks = ({ shown }) => {
  const hasSpark = shown.some((i) => notifications[i]?.isSpark);
  if (!hasSpark) return null;
  return (
    <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden">
      {Array.from({ length: 14 }).map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 400 + Math.random() * 30, x: 30 + Math.random() * 200 }}
          animate={{ opacity: [0, 1, 0], y: -50, x: 30 + Math.random() * 200 + (i % 2 === 0 ? 30 : -30) }}
          transition={{ duration: 3.5 + (i % 4), repeat: Infinity, delay: i * 0.25, ease: 'easeOut' }}
          className="absolute w-1 h-1 rounded-full"
          style={{
            background: i % 3 === 0 ? '#ffa726' : '#4facfe',
            boxShadow: i % 3 === 0 ? '0 0 8px #ffa726' : '0 0 8px #4facfe',
          }}
        />
      ))}
    </div>
  );
};

export default NotificationsSection;
